import { Fragment, useState, useMemo, useCallback } from 'react';

import { Button } from '@mui/material';
import useApi from 'shared/hooks/api';
import api from 'utils/api';
import { PageLoader, PageError, Modal } from 'components';

// components and parts
import Details from '../components/DetailsModal';
import New from './newModal';
import { DOLPHIN_API } from 'shared/constants/apis';
import { tableColumns } from './tableColumns';
import { CONTRACT_TYPE } from './constants';
import BasicTable from 'components/BasicTable';
import { ConfirmModal } from 'shared/modals/confirmModal';
import logger from 'utils/logger';
import { useSnackbar } from 'notistack';

const log = logger.createLogger('Contracts');

const IWGDashboardContracts = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [contractDetailsModalOpen, setContractDetailsModalOpen] =
    useState(false);
  const [contractIdSelected, setContractIdSelected] = useState(-1);
  const [contractDataSelected, setContractDataSelected] = useState({});
  const [contractNewModalOpen, setContractNewModalOpen] = useState(false);
  const [contractDeleteModalOpen, setContractDeleteModalOpen] = useState(false);

  const handleOpenContract = useCallback((contract, type) => {
    setContractIdSelected(contract.id);
    setContractDataSelected(contract);
    type === CONTRACT_TYPE.DELETE
      ? setContractDeleteModalOpen(true)
      : setContractDetailsModalOpen(true);
  }, []);

  const columns = useMemo(
    () => tableColumns({ handleOpenContract }),
    [handleOpenContract]
  );

  const [{ data, error }, fetchContracts] = useApi.useGet(
    DOLPHIN_API.ADMIN_CONTRACTS,
    {}
  );
  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  data.sort((contract1, contract2) => {
    if (contract1.id > contract2.id) return 1;
    if (contract1.id < contract2.id) return -1;
    return 0;
  });

  const tableData = data.map((contract) => {
    return {
      contractId: contract.id,
      srcOrgName: contract.srcOrg.name,
      dstOrgName: contract.dstOrg.name,
      quota: contract.quota,
      used: contract.used,
      monthlyInvoiceDate: contract.monthlyInvoiceDate,
      details: contract,
      delete: contract,
    };
  });

  const handleUpdateContract = async (contractId, updatedContractJSON) => {
    const updatedContractData = JSON.parse(updatedContractJSON);
    await api.put(DOLPHIN_API.ADMIN_CONTRACTS, {
      data: {
        ids: [contractId],
        updatedData: [updatedContractData],
      },
    });
    await fetchContracts();
    enqueueSnackbar('Contract updated successfully', {
      variant: 'success',
    });
  };

  const handleDeleteContract = async (contractId) => {
    try {
      await api.delete(DOLPHIN_API.ADMIN_CONTRACTS, {
        data: {
          ids: [contractId],
        },
      });
      await fetchContracts();
      enqueueSnackbar('Contract deleted successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error deleting contract:', error);
      enqueueSnackbar(
        `Error deleting contract: ${error.message || 'Unknown error occurred'}`,
        {
          variant: 'error',
        }
      );
    } finally {
      setContractDeleteModalOpen(false);
    }
  };

  return (
    <Fragment>
      <BasicTable
        columns={columns}
        data={tableData}
        // TODO: make this dynamic
        darkMode={false}
        tableTitle="Contracts"
        enableRowSelection={false}
        handleRefreshClick={fetchContracts}
        rowsPerPageOptions={[10, 20, 50, 100]}
        initialPageSize={50}
        customToolbar={
          <Button
            color="primary"
            variant="contained"
            onClick={() => setContractNewModalOpen(true)}>
            Create new
          </Button>
        }
      />

      <Modal
        isOpen={contractNewModalOpen}
        width="700px"
        withCloseIcon={true}
        onClose={() => setContractNewModalOpen(false)}
        renderContent={(modal) => (
          <New close={modal.close} fetchContracts={fetchContracts} />
        )}
      />

      <Modal
        isOpen={contractDetailsModalOpen}
        width="700px"
        withCloseIcon={true}
        onClose={() => setContractDetailsModalOpen(false)}
        renderContent={() => (
          <Details
            dataType="Contract"
            dataId={contractIdSelected}
            data={contractDataSelected}
            updateData={handleUpdateContract}
          />
        )}
      />

      <ConfirmModal
        isOpened={contractDeleteModalOpen}
        onCancel={() => setContractDeleteModalOpen(false)}
        onConfirm={() => handleDeleteContract(contractIdSelected)}
        title={`Do you want to DELETE Contract:  From [${contractDataSelected?.srcOrg?.name}] To [${contractDataSelected?.dstOrg?.name}]`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Fragment>
  );
};

export default IWGDashboardContracts;
